import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Brush } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SymptomTrendChart = ({ data, onDateRangeChange }) => {
  const [selectedRange, setSelectedRange] = useState('30d');
  const [isZoomed, setIsZoomed] = useState(false);

  const timeRanges = [
    { key: '7d', label: '7 Days' },
    { key: '30d', label: '30 Days' },
    { key: '90d', label: '90 Days' },
    { key: 'all', label: 'All Time' }
  ];

  const handleRangeChange = (range) => {
    setSelectedRange(range);
    onDateRangeChange?.(range);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{`Date: ${label}`}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {`${entry?.name}: ${entry?.value}/10`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (payload?.severity >= 7) {
      return <circle cx={cx} cy={cy} r={4} fill="#dc2626" stroke="#fff" strokeWidth={2} />;
    }
    return null;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Symptom Severity Trends</h2>
          <p className="text-sm text-gray-600 mt-1">30-day symptom tracking with severity levels (1-10)</p>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          {timeRanges?.map((range) => (
            <Button
              key={range?.key}
              variant={selectedRange === range?.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleRangeChange(range?.key)}
            >
              {range?.label}
            </Button>
          ))}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsZoomed(!isZoomed)}
            iconName={isZoomed ? 'ZoomOut' : 'ZoomIn'}
          >
            {isZoomed ? 'Reset' : 'Zoom'}
          </Button>
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis 
              dataKey="date" 
              stroke="#64748b"
              fontSize={12}
              tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            />
            <YAxis 
              stroke="#64748b"
              fontSize={12}
              domain={[0, 10]}
              tickCount={6}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Severity zones */}
            <defs>
              <linearGradient id="severityGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dc2626" stopOpacity={0.1} />
                <stop offset="30%" stopColor="#f59e0b" stopOpacity={0.1} />
                <stop offset="70%" stopColor="#10b981" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            
            <Line
              type="monotone"
              dataKey="severity"
              stroke="#2563eb"
              strokeWidth={3}
              dot={<CustomDot />}
              activeDot={{ r: 6, fill: '#2563eb' }}
              name="Severity Level"
            />
            
            {isZoomed && (
              <Brush 
                dataKey="date" 
                height={30} 
                stroke="#2563eb"
                tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded-full" />
          <span className="text-sm text-gray-600">Low (1-3)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-yellow-500 rounded-full" />
          <span className="text-sm text-gray-600">Moderate (4-6)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded-full" />
          <span className="text-sm text-gray-600">Severe (7-10)</span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="AlertCircle" size={12} color="#dc2626" />
          <span className="text-sm text-gray-600">High severity markers</span>
        </div>
      </div>
    </div>
  );
};

export default SymptomTrendChart;